import { useMemo } from 'react';
import { Select, Switch, Table, Checkbox, Button, Tooltip } from 'antd';
import { RotateCcw } from 'lucide-react';
import { formatAmount } from '@/utils/helper';
import { STRIKE_COUNT_OPTIONS, STRIKE_SELECTION_OPTIONS, formatOI } from './multi-strike-utils';

const StrikeSelector = ({
  mode,
  allStrikes,
  selectedStrikes,
  strikeTableData,
  criticalPoints,
  atmStrike,
  strikeCount,
  selectionPreset,
  onChangeStrikeCount,
  onChangeSelectionPreset,
  onToggleStrike,
}) => {
  const isStrikeSelected = (strike, type) => {
    const found = selectedStrikes.find((s) => s.strike === strike);
    if (!found) return false;
    if (type === 'ce') return found.enableCE;
    if (type === 'pe') return found.enablePE;
    return found.enableCE || found.enablePE;
  };

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'OI CE',
        dataIndex: 'oiCE',
        key: 'oiCE',
        width: 70,
        onCell: (row) => ({
          className: `ms-col-oi ${row.strike === atmStrike ? '' : row.strike < atmStrike ? 'ms-cell-itm-ce' : 'ms-cell-otm'}`,
        }),
        render: (val, record) => {
          const className = val === criticalPoints.maxOiCE ? 'ms-highlight-max' : val === criticalPoints.minOiCE ? 'ms-highlight-min' : '';
          return <span id={`${record.strike}-oiCE`} className={className}>{formatOI(val)}</span>;
        },
      },
      {
        title: 'LTP CE',
        dataIndex: 'lpCE',
        key: 'lpCE',
        width: 70,
        onCell: (row) => ({
          className: row.strike === atmStrike ? '' : row.strike < atmStrike ? 'ms-cell-itm-ce' : 'ms-cell-otm',
        }),
        render: (val, record) => <span id={`${record.strike}-lpCE`} style={{ color: 'var(--green)' }}>{val ? formatAmount(val.toFixed(2)) : '-'}</span>,
      },
    ];

    if (mode === 'lp' || mode === 'oi') {
      cols.push({
        title: 'CE',
        key: 'chkCE',
        width: 35,
        align: 'center',
        onCell: (row) => ({
          className: row.strike === atmStrike ? '' : row.strike < atmStrike ? 'ms-cell-itm-ce' : 'ms-cell-otm',
        }),
        render: (_, row) => (
          <Checkbox
            checked={isStrikeSelected(row.strike, 'ce')}
            onChange={(e) => onToggleStrike(row.strike, 'ce', e.target.checked)}
          />
        ),
      });
    }

    cols.push({
      title: 'Strike',
      dataIndex: 'strike',
      key: 'strike',
      width: 100,
      align: 'center',
      render: (val, row) => (
        <div className="ms-strike-cell-content">
          {(mode === 'pcr' || mode === 'iv') && (
            <Checkbox
              className="ms-strike-checkbox"
              checked={isStrikeSelected(row.strike, 'strike')}
              onChange={(e) => onToggleStrike(row.strike, 'strike', e.target.checked)}
            />
          )}
          <span
            className={`ms-strike-cell ${val === atmStrike ? 'ms-strike-cell--atm' : ''}`}
          >
            <span
              className="ms-color-dot"
              style={{ backgroundColor: row.colorCE }}
            />
            {val}
            {(mode === 'lp' || mode === 'oi') && (
              <span
                className="ms-color-dot"
                style={{ backgroundColor: row.colorPE }}
              />
            )}
          </span>
        </div>
      ),
    });

    if (mode === 'lp' || mode === 'oi') {
      cols.push({
        title: 'PE',
        key: 'chkPE',
        width: 35,
        align: 'center',
        onCell: (row) => ({
          className: row.strike === atmStrike ? '' : row.strike > atmStrike ? 'ms-cell-itm-pe' : 'ms-cell-otm',
        }),
        render: (_, row) => (
          <Checkbox
            checked={isStrikeSelected(row.strike, 'pe')}
            onChange={(e) => onToggleStrike(row.strike, 'pe', e.target.checked)}
          />
        ),
      });
    }

    cols.push({
      title: 'LTP PE',
      dataIndex: 'lpPE',
      key: 'lpPE',
      width: 70,
      onCell: (row) => ({
        className: row.strike === atmStrike ? '' : row.strike > atmStrike ? 'ms-cell-itm-pe' : 'ms-cell-otm',
      }),
      render: (val, record) => <span id={`${record.strike}-lpPE`} style={{ color: 'var(--red)' }}>{val ? formatAmount(val.toFixed(2)) : '-'}</span>,
    });

    cols.push({
      title: 'OI PE',
      dataIndex: 'oiPE',
      key: 'oiPE',
      width: 70,
      onCell: (row) => ({
        className: `ms-col-oi ${row.strike === atmStrike ? '' : row.strike > atmStrike ? 'ms-cell-itm-pe' : 'ms-cell-otm'}`,
      }),
      render: (val, record) => {
        const className = val === criticalPoints.maxOiPE ? 'ms-highlight-max' : val === criticalPoints.minOiPE ? 'ms-highlight-min' : '';
        return <span id={`${record.strike}-oiPE`} className={className}>{formatOI(val)}</span>;
      },
    });

    if (mode === 'pcr') {
      cols.push({
        title: 'PCR',
        key: 'pcr',
        width: 60,
        render: (_, row) => (
          <span id={`${row.strike}-pcr`}>
            {row.oiCE > 0 ? (row.oiPE / row.oiCE).toFixed(2) : '-'}
          </span>
        ),
      });
    }

    if (mode === 'iv') {
      cols.push({
        title: 'IV',
        key: 'iv',
        width: 60,
        render: (_, row) => (
          <span id={`${row.strike}-iv`}>
            {row.ivCE ? row.ivCE.toFixed(2) : '-'}
          </span>
        ),
      });
    }

    return cols;
  }, [mode, selectedStrikes, atmStrike, criticalPoints]);

  const dataSource = useMemo(
    () =>
      allStrikes.map((strike) => ({
        key: strike,
        strike,
        ...(strikeTableData[strike] || {}),
      })),
    [allStrikes, strikeTableData],
  );

  return (
    <div className="ms-strike-selector">
      {/* Quick actions */}
      <div className="ms-strike-actions">
        <div className="config-group">
          <label>Strikes</label>
          <Select
            options={STRIKE_COUNT_OPTIONS}
            style={{ width: 100 }}
            onChange={onChangeStrikeCount}
            value={strikeCount}
          />
        </div>

        <div className="config-group">
          <label>Selection</label>
          <Select
            options={STRIKE_SELECTION_OPTIONS}
            style={{ width: 130 }}
            onChange={onChangeSelectionPreset}
            value={selectionPreset}
          />
        </div>

        <Tooltip title="Reset to Only ATM">
          <Button
            icon={<RotateCcw size={14} />}
            onClick={() => onChangeSelectionPreset('only-atm')}
            className='ms-reset-strike-btn'
          />
        </Tooltip>
      </div>

      {/* Strike table */}
      <div className="ms-strike-table-wrapper">
        <Table
          className="ms-strike-table"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          bordered
          scroll={{ y: 'calc(100vh - 240px)' }}
          rowClassName={(row) =>
            row.strike === atmStrike ? 'ms-row-atm' : ''
          }
        />
      </div>
    </div>
  );
};

export default StrikeSelector;
